import React, { useEffect, useState } from "react";
import { Dialog, Box, Avatar, Typography, IconButton, Divider, TextField, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VideoApiService from "../../services/video-api-service";
import VideoComments from "../../pages/VideoPlayer/VideoComments";
import { getUserDetail } from "../../constants/function";
import notifier from "../../components/Notifier";

const GOLD = "#FFEE20";

const PANEL_W = 400;

function uploaderName(u) {
  if (!u) return "Unknown";
  return (
    u.displayArtistName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown"
  );
}

// Expanded view for a feed video: comments on the left, video on the right
// (stacked on mobile). Reuses the existing VideoComments thread + the video
// like API. Mirrors GalleryDetailModal so the two media types feel the same.
export default function VideoDetailModal({ item, open, onClose, onItemUpdate }) {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentUser = getUserDetail();

  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [description, setDescription] = useState("");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");

  useEffect(() => {
    if (item) {
      setLiked(Boolean(item.isLiked));
      setLikesCount(item.likesCount || 0);
      setDescription(item.description || "");
    }
  }, [item]);

  if (!item) return null;

  const uploader = item.uploader || {};
  const uploaderId = uploader._id || uploader.uId || uploader.id;
  const avatar = item.profileImg || (uploader.profileImg && uploader.profileImg.filePath);
  const isOwner = currentUser && uploaderId && String(currentUser._id) === String(uploaderId);

  const saveDescription = async () => {
    const next = descDraft.slice(0, 2000);
    try {
      await VideoApiService.updateVideo(item._id, { description: next });
      setDescription(next);
      setEditingDesc(false);
    } catch (e) {
      notifier.error("Failed to update description");
    }
  };

  const goToProfile = () => {
    if (uploaderId) history.push(`/app/ArtistProfile/${uploaderId}`);
  };

  const handleLike = async () => {
    const next = !liked;
    setLiked(next);
    setLikesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      const res = next ? await VideoApiService.likeVideo(item._id) : await VideoApiService.unlikeVideo(item._id);
      const data = (res && (res.result || res.data)) || null;
      if (data && typeof data.likesCount === "number") {
        setLiked(Boolean(data.liked));
        setLikesCount(data.likesCount);
        if (onItemUpdate) onItemUpdate(item._id, { isLiked: data.liked, likesCount: data.likesCount });
      }
    } catch (e) {
      setLiked(!next);
      setLikesCount((c) => Math.max(0, c + (next ? -1 : 1)));
    }
  };

  const videoPanel = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: isMobile ? "40vh" : "auto",
        maxHeight: isMobile ? "55vh" : "none",
      }}
    >
      <video
        src={item.filePath}
        poster={item.thumbnail || undefined}
        controls
        playsInline
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
      />
    </Box>
  );

  const detailsPanel = (
    <Box
      sx={{
        width: isMobile ? "100%" : PANEL_W,
        flexShrink: 0,
        height: isMobile ? "auto" : "100%",
        overflowY: isMobile ? "visible" : "auto",
        borderRight: isMobile ? "none" : "1px solid #1c1c24",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
    >
      {/* Uploader header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <Avatar
          src={avatar}
          sx={{ width: 40, height: 40, cursor: uploaderId ? "pointer" : "default" }}
          onClick={goToProfile}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 15, fontWeight: 700, color: "#fff", cursor: uploaderId ? "pointer" : "default" }}
            onClick={goToProfile}
          >
            {uploaderName(uploader)}
          </Typography>
          {item.releaseDate ? (
            <Typography sx={{ fontSize: 12, color: "#888" }}>
              {new Date(item.releaseDate).toLocaleDateString()}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Title */}
      {item.title ? (
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#eee", mb: 1 }}>{item.title}</Typography>
      ) : null}

      {/* Description (owner can edit) */}
      {editingDesc ? (
        <Box sx={{ mb: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            placeholder="Add a description…"
            sx={{
              "& .MuiOutlinedInput-root": { bgcolor: "#1a1a1a", color: "#fff", borderRadius: "10px" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
            }}
          />
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <Button size="small" onClick={() => setEditingDesc(false)} sx={{ color: "#aaa", textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveDescription}
              sx={{ background: GOLD, color: "#000", textTransform: "none", "&:hover": { background: "#FFE066" } }}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
          {description ? (
            <Typography sx={{ fontSize: 14, color: "#ccc", whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1 }}>
              {description}
            </Typography>
          ) : (
            isOwner && <Typography sx={{ fontSize: 14, color: "#777", flex: 1 }}>Add a description…</Typography>
          )}
          {isOwner && (
            <IconButton
              size="small"
              onClick={() => {
                setDescDraft(description);
                setEditingDesc(true);
              }}
              sx={{ color: "#888" }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      )}

      {/* Like */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <IconButton size="small" onClick={handleLike} sx={{ color: liked ? "#ff4d6d" : "#bbb" }}>
          {liked ? <FavoriteIcon sx={{ fontSize: 22 }} /> : <FavoriteBorderIcon sx={{ fontSize: 22 }} />}
        </IconButton>
        <Typography sx={{ fontSize: 14, color: "#ccc" }}>{likesCount}</Typography>
      </Box>

      <Divider sx={{ borderColor: "#1c1c24", mb: 1.5 }} />

      {/* Comments (existing video comment thread) */}
      <Box sx={{ flex: 1 }}>
        <VideoComments videoId={item._id} commentCount={""} />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "#0d0d12",
          color: "#fff",
          m: isMobile ? 0 : 2,
          width: isMobile ? "100%" : "min(1040px, 94vw)",
          height: isMobile ? "100%" : "90vh",
          maxWidth: "none",
          borderRadius: isMobile ? 0 : "14px",
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.45)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {isMobile ? (
        <Box sx={{ height: "100%", overflowY: "auto" }}>
          {videoPanel}
          {detailsPanel}
        </Box>
      ) : (
        <Box sx={{ display: "flex", height: "100%" }}>
          {detailsPanel}
          {videoPanel}
        </Box>
      )}
    </Dialog>
  );
}
